import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import { push } from "connected-react-router";

import moment from "moment";

import * as DatasetAPI from "api/DatasetAPI";

import { CHART_TYPES } from "containers/Report/ReportReducer";

import * as setupReducer from "containers/Setup/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as nextModelsReducer from "containers/Next/Models/ModelsReducer";
import * as homeReducer from "containers/Home/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as marketReducer from "containers/Market/MarketReducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as globalReducer from "containers/App/reducer";
import { delay } from "utils/promise";

//location of the hex that appears after creating AI or attaching a blueprint
const TRANSFORM_HEX_LOCATION = {
  CREATE: [{ x: 8, y: 10 }],
  BLUEPRINT: [{ x: 10, y: 9 }]
};

const UPDATE_FIELD = "nextTransform/UPDATE_FIELD";
const UPDATE_FIELD_VALUE = "nextTransform/UPDATE_FIELD_VALUE";
const UPDATE_HEX = "nextTransform/UPDATE_HEX";
const UPDATE_HOVER = "nextTransform/UPDATE_HOVER";

const initialNext = {};

function transformReducer(state = initialNext, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    default:
      return state;
      "";
  }
}

/* Selectors */
export function getBase(state) {
  return state.nextTransform;
}

export function getTransform(state, id) {
  return getBase(state)[id] ? getBase(state)[id] : {};
}

export function getSelectedHex(state, id) {
  return getTransform(state, id).selectedHex;
}

export function getActionHexMap(state, id) {
  return getTransform(state, id).actionHexMap;
}

export function getHexMap(state, id) {
  return getTransform(state, id).hexMap;
}

export function getSavedHexMap(state, id) {
  return getTransform(state, id).savedHexMap;
}

export function getHexSize(state, id) {
  return getTransform(state, id).hexSize;
}

export function getInnerHexSize(state, id) {
  return getTransform(state, id).innerHexSize;
}

export function getHexagonRadius(state, id) {
  return getTransform(state, id).hexagonRadius;
}

export function getHoverGroup(state, id) {
  return getTransform(state, id).hoverGroup;
}

export function getHoverRibbon(state, id) {
  return getTransform(state, id).hoverRibbon;
}

export function getIsCreating(state, id) {
  return getTransform(state, id).isCreating;
}

export function getNodeFromHex(state, id, hex) {
  const hexMap = getHexMap(state, id);
  const actionHexMap = getActionHexMap(state, id);

  return actionHexMap[hex.x + "_" + hex.y] || hexMap[hex.x + "_" + hex.y];
}

export function getSelectedNode(state, id) {
  const selectedHex = getSelectedHex(state, id);

  let selectedNode = undefined;

  if (selectedHex) {
    const actionHexMap = getActionHexMap(state, id);
    const hexMap = getHexMap(state, id);
    const savedHexMap = getSavedHexMap(state, id);

    selectedNode =
      actionHexMap[selectedHex.x + "_" + selectedHex.y] ||
      hexMap[selectedHex.x + "_" + selectedHex.y] ||
      savedHexMap[selectedHex.x + "_" + selectedHex.y];
  }

  return selectedNode;
}

export function getSelectedSection(state, id) {
  const selectedNode = getSelectedNode(state, id);

  return selectedNode?.leftSection;
}

export function getAllHex(state, id) {
  return {
    ...getSavedHexMap(state, id),
    ...getHexMap(state, id),
    ...getActionHexMap(state, id)
  };
}

export function getNeighborFromPosition(state, id, x, y, sx = undefined, sy = undefined) {
  const hexagonRadius = getHexagonRadius(state, id);

  const startX = sx !== undefined ? sx : hexagonRadius;
  const startY = sy !== undefined ? sy : hexagonRadius;

  let yDelta = -1;

  if (startX % 2 !== 0) {
    yDelta = 1;
  }

  const fieldIndex =
    x === startX && y === startY - 1
      ? 0
      : x === startX + 1 && y === startY + yDelta
        ? 1
        : x === startX + 1 && y === startY
          ? 2
          : x === startX && y === startY + 1
            ? 3
            : x === startX - 1 && y === startY
              ? 4
              : x === startX - 1 && y === startY + yDelta
                ? 5
                : undefined;

  return fieldIndex;
}

export function getFieldFromPosition(state, id, x, y, sx = undefined, sy = undefined) {
  const datasetId = nextReducer.getDatasetId(state, id);

  const rankedFields = homeReducer.getRankedFields(state, datasetId);
  const neighborIndex = getNeighborFromPosition(state, id, x, y, sx, sy);

  return neighborIndex !== undefined && rankedFields !== undefined ? rankedFields[neighborIndex] : undefined;
}

export function getSelectedNeightbors(state, id, x, y) {
  const selectedHex = getSelectedHex(state, id);

  const hexagonRadius = getHexagonRadius(state, id);

  return selectedHex === undefined ? undefined : getFieldFromPosition(state, id, x, y, hexagonRadius, hexagonRadius);
}

export function getDoubleFieldFromPosition(state, id, x, y) {
  const hexagonRadius = getHexagonRadius(state, id);
  const currentField = getFieldFromPosition(state, id, x, y);

  return currentField
    ? undefined
    : x === hexagonRadius + 1 && y === hexagonRadius - 2
      ? [getFieldFromPosition(state, id, x - 1, y + 1), getFieldFromPosition(state, id, x, y + 1)]
      : x === hexagonRadius + 2 && y === hexagonRadius
        ? [getFieldFromPosition(state, id, x - 1, y - 1), getFieldFromPosition(state, id, x - 1, y)]
        : x === hexagonRadius + 2 && y === hexagonRadius
          ? [getFieldFromPosition(state, id, x - 1, y - 1), getFieldFromPosition(state, id, x - 1, y)]
          : x === hexagonRadius + 1 && y === hexagonRadius + 1
            ? [getFieldFromPosition(state, id, x, y - 1), getFieldFromPosition(state, id, x - 1, y)]
            : x === hexagonRadius - 1 && y === hexagonRadius + 1
              ? [getFieldFromPosition(state, id, x + 1, y), getFieldFromPosition(state, id, x, y - 1)]
              : x === hexagonRadius - 2 && y === hexagonRadius
                ? [getFieldFromPosition(state, id, x + 1, y), getFieldFromPosition(state, id, x + 1, y - 1)]
                : x === hexagonRadius - 1 && y === hexagonRadius - 2
                  ? [getFieldFromPosition(state, id, x, y + 1), getFieldFromPosition(state, id, x + 1, y + 1)]
                  : undefined;
}

export function getHexagonFields(state, id, hex) {
  const firstOrderField = getFieldFromPosition(state, id, hex.x, hex.y);
  const doubleOrderFields = undefined; //getDoubleFieldFromPosition(state, id, hex.x, hex.y);
  const selectedNeighbors = getSelectedNeightbors(state, id, hex.x, hex.y);

  const hexMap = getHexMap(state, id);
  const hKey = hex.x + "_" + hex.y;

  return hexMap[hKey]
    ? hexMap[hKey].fields
    : firstOrderField
      ? { 0: firstOrderField }
      : doubleOrderFields
        ? { 0: doubleOrderFields[0], 1: doubleOrderFields[1] }
        : selectedNeighbors
          ? { 0: selectedNeighbors }
          : {};
}

export function getChartsFromDirection(state, id, fields, direction) {
  const charts = [];

  const viewIndex = getViewIndex(state, id);

  for (let i = viewIndex; i < 30; i++) {
    if (direction === HEXAGON_NODES.FIRST_VARIABLE && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (direction === HEXAGON_NODES.SECOND_VARIABLE && Object.keys(fields).length > 1) {
      charts.push(getSecondOrder(state, id, fields[1], i));
    } else if (direction === HEXAGON_NODES.DRILL_IN && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (direction === HEXAGON_NODES.DRILL_IN_CLUSTER && Object.keys(fields).length > 0) {
      charts.push(getSecondOrder(state, id, fields[0], i));
    } else if (Object.keys(fields).length > 0) {
      charts.push(getRandomSecondOrder(state, id, fields[0], i));
    }
  }

  return charts;
}

export function getNeighborLocation(x, y) {
  let secondItemY = 0;
  let thirdItemY = 1;
  let lastItemY = 0;

  if (x % 2 === 0) {
    secondItemY = -1;
    thirdItemY = 0;
    lastItemY = -1;
  }

  return {
    [0]: {
      x: x,
      y: y - 1
    },
    [1]: {
      x: x + 1,
      y: y + secondItemY
    },
    [2]: {
      x: x + 1,
      y: y + thirdItemY
    },
    [3]: {
      x: x,
      y: y + 1
    },
    [4]: {
      x: x - 1,
      y: y + thirdItemY
    }
    // [5]: {
    //   x: x - 1,
    //   y: y + lastItemY,
    // },
  };
}

export function getDistanceFromNode(aX, aY, bX, bY) {
  return Math.abs(aX - bX) + Math.abs(aY - bY);
}

export function getDoubleNeighborLocation(state, id, x, y) {
  const hexMap = getHexMap(state, id);
  const savedHexMap = getSavedHexMap(state, id);

  const neighbors = getNeighborLocation(x, y);

  let retNeighbors = [];

  for (const nKey of Object.keys(neighbors)) {
    const neighbor = neighbors[nKey];

    const nextNeighbors = getNeighborLocation(neighbor.x, neighbor.y);

    for (const iKey of Object.keys(nextNeighbors)) {
      const nextNeighbor = nextNeighbors[iKey];

      if (
        hexMap &&
        hexMap[nextNeighbor.x + "_" + nextNeighbor.y] === undefined &&
        savedHexMap[nextNeighbor.x + "_" + nextNeighbor.y] === undefined &&
        retNeighbors.find((n) => n.x === nextNeighbor.x && n.y === nextNeighbor.y) === undefined
      ) {
        retNeighbors.push(nextNeighbor);
      }
    }
  }

  return retNeighbors.sort((a, b) => {
    if (getNeighborFromPosition(state, id, a.x, a.y, x, y) !== undefined) {
      return -1;
    } else if (getNeighborFromPosition(state, id, b.x, b.y, x, y) !== undefined) {
      return 1;
    }

    return getDistanceFromNode(a.x, a.y, x, y) > getDistanceFromNode(b.x, b.y, x, y)
      ? 1
      : getDistanceFromNode(a.x, a.y, x, y) < getDistanceFromNode(b.x, b.y, x, y)
        ? -1
        : 0;
  });
}

export function getTransformedField(state, id, field) {
  const datasetId = nextReducer.getDatasetId(state, id);

  const dataset = homeReducer.getDataset(state, datasetId);
  const rankedFields = homeReducer.getRankedFields(state, datasetId);

  const fIndex = rankedFields.findIndex((f) => f.label === field);

  if (fIndex === -1) {
    return undefined;
  }

  return {
    ...dataset.fields[field],
    rank: fIndex,
    color: colors[fIndex % colors.length] ?? "gray"
  };
}

export function updateHexMap(id, hexMap) {
  return { type: UPDATE_FIELD_VALUE, id: id, key: "hexMap", data: hexMap };
}

export function updateSavedHexMap(id, savedHexMap) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "savedHexMap",
    data: savedHexMap
  };
}

export function updateActionHexMap(id, actionHexMap) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "actionHexMap",
    data: actionHexMap
  };
}

export function updateCenterHex(id, centerHex) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "centerHex",
    data: centerHex
  };
}

export function updateIsCreating(id, isCreating) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    key: "isCreating",
    data: isCreating
  };
}

export function getInitialHexMap(state, id) {
  const hexagonRadius = 8;
  let hexMap = { ...initTransform.hexMap };

  const neighbors = getNeighborLocation(hexagonRadius, hexagonRadius);

  const node = hexMap[hexagonRadius + "_" + hexagonRadius];

  let nIndex = 0;
  for (let nKey of Object.keys(neighbors)) {
    const neighbor = neighbors[nKey];

    if (node && node.selected) {
      if (node.selected[nIndex]) {
        hexMap[neighbor.x + "_" + neighbor.y] = {
          ...node.selected[nIndex],
          parent: hex,
          color: node.color
        };
      }
    } else {
      if (getTopActions(state, id)[nIndex]) {
        hexMap[neighbor.x + "_" + neighbor.y] = {
          ...getTopActions(state, id)[nIndex]
        };
      }
    }

    nIndex++;
  }

  return hexMap;
}

export function handleBack(id) {
  return (dispatch, getState) => {
    const selectedNode = getSelectedNode(getState(), id);

    if (selectedNode?.parent && false) {
      return dispatch(updateSelectedHex(id, selectedNode?.parent, selectedNode?.parent));
    } else {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: undefined
      });

      dispatch(updateActionHexMap(id, {}));

      return dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
    }
  };
}

export function handleUpdate(id, hex, fNode = undefined) {
  return (dispatch, getState) => {
    const hexMap = getHexMap(getState(), id);
    let savedHexMap = { ...getSavedHexMap(getState(), id) };

    let hKey = hex.x + "_" + hex.y;

    if (hexMap[hex.x + "_" + hex.y] !== undefined) {
      const hexKey = hex?.leftSection ?? fNode?.leftSection ?? "BLUEPRINT";
      const hexLocations = TRANSFORM_HEX_LOCATION[hexKey] ?? TRANSFORM_HEX_LOCATION.CREATE;

      const nHex = hexLocations?.find((h) => savedHexMap[h.x + "_" + h.y] === undefined) ?? hex;

      hKey = nHex.x + "_" + nHex.y;
    }

    const projectId = nextReducer.getProjectId(getState(), id);
    const runId = projectId ? projectReducer.getRunId(getState(), projectId) : "new";

    const node = fNode != undefined ? fNode : getNodeFromHex(getState(), id, hex);

    savedHexMap[hKey] = {
      ...node,
      afterSetup: { ...setupReducer.getSetup(getState(), runId) }
    };

    if (node?.leftSection !== "BLUEPRINT") {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: undefined
      });
    }

    dispatch(updateActionHexMap(id, {}));
    dispatch(updateSavedHexMap(id, savedHexMap));
    dispatch(updateCenterHex(id, hex));
    return dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
  };
}

export function nextHexDelta(hex, delta, oDelta) {
  // const retHex = {
  //   x:
  //     hex.x +
  //     (oDelta === undefined ? delta.x : hex.x % 2 !== 0 ? delta.x : oDelta.x),
  //   y:
  //     hex.y +
  //     (oDelta === undefined ? delta.y : hex.y % 2 !== 0 ? delta.y : oDelta.y),
  // };
  const retHex = {
    x: hex.x,
    //(oDelta === undefined ? delta.x : hex.x % 2 !== 0 ? delta.x : oDelta.x),
    y: hex.y - 1
    //(oDelta === undefined ? delta.y : hex.y % 2 !== 0 ? delta.y : oDelta.y),
  };
  return retHex;
}

export function findNodeInSaved(state, id, node) {
  const savedHexMap = getSavedHexMap(state, id) ?? {};

  const fKey = Object.keys(savedHexMap).find(
    (h) => savedHexMap[h]?.id !== undefined && savedHexMap[h]?.id === node?.id
  );

  return fKey === undefined ? undefined : savedHexMap[fKey];
}

export function handleUpdateCreateExplore(id, node) {
  return (dispatch, getState) => {
    let retSavedHexMap = { ...getSavedHexMap(getState(), id) };

    if (retSavedHexMap[node.x + "_" + node.y] !== undefined) {
      retSavedHexMap[node.x + "_" + node.y].isLoading = false;
      retSavedHexMap[node.x + "_" + node.y].hexMap = {
        ...nextReducer.getHexMap(getState(), id)
      };
    }

    dispatch(updateSavedHexMap(id, retSavedHexMap));
  };
}

export function deleteExplorationNode(id, node) {
  return (dispatch, getState) => {
    const shouldDelete = confirm("You are about to delete this exploration. Are you sure you want to do this?");

    if (shouldDelete) {
      const savedHexMap = { ...getSavedHexMap(getState(), id) };

      const fKey = Object.keys(savedHexMap).find(
        (s) => savedHexMap[s]?.text === node?.text && node?.text !== undefined
      );

      if (fKey) {
        delete savedHexMap[fKey];

        dispatch(updateSavedHexMap(id, savedHexMap));
      }
    }

    return Promise.resolve();
  };
}

export function handleCreateExplore(id, node) {
  return (dispatch, getState) => {
    const parent = node?.parent;

    dispatch(handleBack(id));

    const savedHexMap = { ...getSavedHexMap(getState(), id) };
    const bSavedHexMap = { ...getSavedHexMap(getState(), id) };

    const explorations = reportReducer.getExplorations(getState(), id);
    const explorationId = Object.keys(explorations).length;

    let saveHex = nextHexDelta(node?.parent, node?.parent?.childDelta, node?.parent?.oChildDelta);

    // Try to find an empty spot a maximum of 20 times
    let foundSave = false;

    if (findNodeInSaved(getState(), id, node) === undefined) {
      for (let i = 0; i < 20; i++) {
        if (bSavedHexMap[saveHex.x + "_" + saveHex.y] === undefined) {
          savedHexMap[saveHex.x + "_" + saveHex.y] = {
            ...saveHex,
            ...node,
            isPurple: false,
            isLoading: true,
            leftSection: "OPEN_EXPLORE",
            explorationId: explorationId,
            nextId: id,
            text: "Exploration " + node?.gridType + " " + explorationId,
            index: Object.keys(savedHexMap).length
          };
          foundSave = true;
          break;
        } else {
          saveHex = nextHexDelta(saveHex, node?.parent?.childDelta, node?.parent?.oChildDelta);
        }
      }
    }

    if (foundSave) {
      dispatch(updateSavedHexMap(id, savedHexMap));
    }

    return saveHex;
  };
}

export function handleAddBlueprint(id, blueprintId) {
  return (dispatch, getState) => {
    const hexMap = getHexMap(getState(), id);
    const bNodeKey = Object.keys(hexMap ?? {}).find((hKey) => hexMap[hKey].leftSection === "BLUEPRINT");

    if (bNodeKey) {
      const bNode = hexMap[bNodeKey];

      const keySplit = bNodeKey.split("_");
      const hex = { x: keySplit?.[0], y: keySplit?.[1] };
      dispatch(updateSelectedHex(id, hex, bNode));

      dispatch(blueprintReducer.setBlueprint("new", blueprintId, false, false, true));

      dispatch(handleUpdate(id, hex));
    }

    return Promise.resolve();
  };
}

export function createProjectHelper(id, hex, n, forceCreate = false, setupId = "new", skipRedirect = false) {
  return (dispatch, getState) => {
    const selectedHex = getSelectedHex(getState(), id);

    const comparisonOptions = n?.comparisonOptions;
    // Press create from AI Create Menu
    if (forceCreate || selectedHex !== undefined) {
      dispatch(updateIsCreating(id, true));
      if (hex) {
        dispatch(handleUpdate(id, hex, n));
      }

      const projects = nextReducer.getProjects(getState(), id);
      dispatch(updateIsCreating(id, false));

      console.log("what is projects", projects);

      if (projects && n?.success?.value && projects[n?.success?.value]) {
        const retProjectId = projects[n?.success?.value];

        dispatch(nextReducer.updateProjectId(id, retProjectId));
        dispatch(nextReducer.updateActiveTab(id, "MODELS"));
      } else if (projects && n?.success?.value !== undefined && projects[n?.success?.value] === undefined) {
        let forecastPeriodsOverride;
        if (n?.success?.isForecast) {
          const forecastCol = n?.success?.column;
          dispatch(setupReducer.updateIsForecastH(setupId, true));
          dispatch(setupReducer.updateForecastTimeCol(setupId, forecastCol));

          // Update setupReducer with forecastPeriods for new forecasts if forecastPeriods is different than default value
          const nextId = nextReducer.getCurrentNextId(getState());
          const forecastPeriods = nextReducer.getForecastPeriods(getState(), nextId);
          if (forecastPeriods && forecastPeriods !== nextReducer.DEFAULT_FORECAST_PERIODS) {
            forecastPeriodsOverride = forecastPeriods;
          }
        }

        const datasetId = nextReducer.getDatasetId(getState(), id);
        const setup = setupReducer.getSetup(getState(), setupId);

        console.log("what is datasetId setup", datasetId, setup, n);

        const setupDatasetId = setupReducer.getDatasetId(getState(), setupId);
        if (Number(setupDatasetId) !== Number(datasetId)) {
          dispatch(setupReducer.updateDatasetId(setupId, datasetId));
        }

        //dispatch(setupReducer.initNewSetup(setupId, datasetId));

        if (n.success.type !== "number") {
          dispatch(setupReducer.updateSuccess(setupId, n.success));
        }

        dispatch(setupReducer.checkCounts(setupId, undefined, n.success));

        if (comparisonOptions?.dateField) {
          dispatch(setupReducer.updateComparisonOptions(setupId, comparisonOptions));
        }

        // Check target column and subgrouping and save in report
        // IMPORTANT: keep this synced when saving
        dispatch(nextReducer.checkAndUpdateProjectTargetAndSubgroup(id));

        return dispatch(
          setupReducer.initJobFromWizard(setupId, undefined, {
            nextId: id,
            forecastPeriodsOverride: forecastPeriodsOverride
          })
        ).then((projectId) => {
          dispatch(updateIsCreating(id, false));

          const projects = { ...nextReducer.getProjects(getState(), id) };

          projects[n.success.value] = projectId;
          dispatch(nextReducer.updateProjects(id, projects));

          dispatch(nextReducer.updateProjectId(id, projectId));

          dispatch(nextModelsReducer.init(id));
          dispatch(nextReducer.updateActiveTab(id, "MODELS"));

          if (setup?.blueprintId !== undefined) {
            // Add blueprint steps
            dispatch(nextModelsReducer.updateSelectedHex(id, { x: 8, y: 7 }));

            // Blueprint
            dispatch(nextModelsReducer.updateSelectedHex(id, { x: 8, y: 6 }));
            dispatch(nextModelsReducer.handleUpdate(id, { x: 8, y: 6 }));
            dispatch(nextModelsReducer.handleBack(id));

            const blueprint = blueprintReducer.getBlueprint(getState(), setup?.blueprintId);

            const todos = blueprint?.wizard?.nextObj?.todos
              ? blueprint?.wizard?.nextObj?.todos
              : (blueprint?.wizard?.setup?.todos ?? {});

            todos &&
              Object.keys(todos).forEach((t) => {
                const todo = todos[t];

                if (todo?.parent) {
                  dispatch(
                    nextModelsReducer.updateSelectedHex(id, {
                      x: todo?.parent?.x,
                      y: todo?.parent?.y
                    })
                  );

                  dispatch(nextModelsReducer.handleStartBreadcrumbs(id, todo));

                  dispatch(handleBack(id));
                }
              });

            /*
            if (blueprint?.name.indexOf("Fair") !== -1) {
              // Add fairness group step
              dispatch(
                nextModelsReducer.updateSelectedHex(id, { x: 9, y: 7 })
              );

              // Add fairness step to todo
              const modelsHexMap = nextModelsReducer.getHexMap(
                getState(),
                id
              );
              const fairKey = Object.keys(modelsHexMap).find(
                (aKey) => modelsHexMap[aKey]?.id === "FAIR"
              );
              console.log(
                "MODELS ACTION FAIR GUIDELINES",
                modelsHexMap,
                fairKey
              );

              if (fairKey !== undefined) {
                dispatch(
                  nextModelsReducer.handleStartBreadcrumbs(
                    id,
                    modelsHexMap[fairKey],
                    true
                  )
                );
              }

              dispatch(nextModelsReducer.handleBack(id));
            } else {
              // Add sales group step
              dispatch(
                nextModelsReducer.updateSelectedHex(id, { x: 8, y: 7 })
              );

              // Add fairness step to todo
              const modelsHexMap = nextModelsReducer.getHexMap(
                getState(),
                id
              );
              const fairKey = Object.keys(modelsHexMap).find(
                (aKey) => modelsHexMap[aKey]?.id === "OPTIMIZE"
              );

              if (fairKey !== undefined) {
                dispatch(
                  nextModelsReducer.handleStartBreadcrumbs(
                    id,
                    modelsHexMap[fairKey],
                    true
                  )
                );
              }

              dispatch(nextModelsReducer.handleBack(id));
            }
            */
          }

          if (setup?.splitColumn !== undefined) {
            dispatch(nextModelsReducer.updateSelectedHex(id, { x: 9, y: 7 }));

            dispatch(nextModelsReducer.updateSelectedHex(id, { x: 9, y: 6 }));
            dispatch(nextModelsReducer.handleUpdate(id, { x: 9, y: 6 }));

            dispatch(nextModelsReducer.handleBack(id));
          }

          dispatch(nextModelsReducer.updateIsTodo(id, false));

          dispatch(chatGPTReducer.handleOptimizeReportDefault(projectId));
          dispatch(chatGPTReducer.handleOptimizeJobWaiter());

          return dispatch(nextReducer.saveNext(id, "New AI Creation Started", false, skipRedirect));
        });
      }
    }

    // Select AI create node.
    // TODO: Add multiple possible projectIds
    if (selectedHex === undefined) {
      dispatch(nextReducer.updateActiveTab(id, "MODELS"));
    }

    return Promise.resolve();
  };
}

export function createDefaultModels(id, datasetId, outcomeSuccess, comparisonOptions) {
  return (dispatch, getState) => {
    const dataset = homeReducer.getDataset(getState(), datasetId);

    const outcome = dataset?.outcome && dataset?.fields ? dataset?.fields?.[dataset?.outcome] : undefined;
    const success =
      outcome.type == "number"
        ? {
            type: outcome.type,
            value: outcome.label
          }
        : (outcomeSuccess ?? outcome?.data?.[0]);

    const node = {
      text: "Create AI Models",
      subText: "Predict" + (outcome.type !== "number" ? ` ${outcome?.label} is` : ""),
      boldText: outcome.type !== "number" ? success?.value : outcome?.label,
      success: success,
      afterText: "Creating AI",
      leftSection: "CREATE",
      color: "white",
      x: 8,
      y: 6,
      parent: {
        x: 8,
        y: 9,
        childDelta: { x: 1, y: 0 },
        oChildDelta: { x: 1, y: 1 }
      },
      comparisonOptions
    };

    const hex = { x: 9, y: 8 };

    const setup = setupReducer.getSetup(getState(), `data_${datasetId}`);

    if (setup && Object.keys(setup).length === 0) {
      dispatch(setupReducer.initNewSetup(`data_${datasetId}`, datasetId));
    }

    dispatch(updateSelectedHex(id, { x: 8, y: 9 }));

    return dispatch(createProjectHelper(id, hex, node, true, `data_${datasetId}`));
  };
}

export function createDefaultExplore(
  id,
  comparisonOptions,
  skipUpdateTab = false,
  minimumValueCount,
  isChatGPT = false,
  allowSkipSecuritySettings = false,
  skipAutoSave = false,
  skipAskIntro = false,
  blueprintId = undefined
) {
  return async (dispatch, getState) => {
    const node = {
      text: "I want Aible to explore all insights",
      isSave: true,
      leftSection: "CREATE_EXPLORE",
      gridType: "Default",
      isPurple: true,
      icon: "Compass",
      x: 8,
      y: 6,
      parent: {
        x: 8,
        y: 7,
        childDelta: { x: 1, y: 0 },
        oChildDelta: { x: 1, y: 1 }
      },
      comparisonOptions
    };

    return await dispatch(
      fullCreateExplore(
        id,
        node,
        skipUpdateTab,
        minimumValueCount,
        isChatGPT,
        allowSkipSecuritySettings,
        skipAutoSave,
        skipAskIntro,
        blueprintId
      )
    );
  };
}

export function fullCreateExplore(
  id,
  n,
  skipUpdateTab = false,
  minimumValueCount,
  isChatGPT = false,
  allowSkipSecuritySettings = false,
  skipAutoSave = false,
  skipAskIntro = false,
  blueprintId = undefined
) {
  return async (dispatch, getState) => {
    const fBlueprint = blueprintReducer.getBlueprint(getState(), blueprintId);

    const datasetId = nextReducer.getDatasetId(getState(), id);
    if (datasetId === "new" || datasetId === undefined) {
      if (skipUpdateTab !== true) {
        if (isChatGPT) {
          const chatApp = chatGPTReducer.getChatApp(getState());
          const currentActiveTab = nextReducer.getActiveTab(getState(), id);
          if (chatApp?.wizard?.startInDashboard === true) {
            dispatch(nextReducer.updateActiveTab(id, "NLQ Dashboard"));
          } else if (currentActiveTab !== "CHATGPT" && currentActiveTab !== "NLQ Dashboard") {
            dispatch(nextReducer.updateActiveTab(id, "CHATGPT"));
          }
        } else if (comparisonOptions) {
          dispatch(nextReducer.updateActiveTab(id, fBlueprint?.wizard?.appStartViewType ?? "DASHBOARD"));
        } else {
          const defaultView = nextReducer.getExploreDefaultViewMode(getState());
          dispatch(nextReducer.updateActiveTab(id, defaultView));
        }
      }
      const isTemplateLoading = globalReducer.getIsTemplateLoading(getState());
      if (isTemplateLoading) {
        dispatch(globalReducer.updateIsTemplateLoading(false));
      }
      return Promise.resolve();
    }

    let saveHex = dispatch(handleCreateExplore(id, n));

    const comparisonOptions = n?.comparisonOptions;

    dispatch(nextReducer.updateComparisonOptions(id, comparisonOptions));
    if (minimumValueCount !== undefined || isChatGPT) {
      let retMinVal = minimumValueCount;
      if (isChatGPT) {
        const chatType = chatGPTReducer.getChatType(getState());
        // We want to force minimumValueCount to be 0 for what's changed, and then only use saved value on isChatGPT and if minimumValueCount is undefined
        if (chatType === "WHATS_CHANGED") {
          retMinVal = 0;
        } else {
          const savedMinValCount = loginReducer.getChatAibleSecurityOption(getState(), "minimumValueCount");
          if (!isNaN(savedMinValCount) && minimumValueCount === undefined) {
            retMinVal = Number(savedMinValCount);
          }
        }
      }

      dispatch(nextReducer.updateMinimumValueCount(id, retMinVal));
    }
    // Check target column and subgrouping and save in report
    // IMPORTANT: keep this synced when saving
    dispatch(nextReducer.checkAndUpdateProjectTargetAndSubgroup(id));

    if (n?.gridType !== undefined) {
      return dispatch(nextReducer.updateGridType(id, n?.gridType, true, comparisonOptions))
        .then(() => delay(1000))
        .then((r) => {
          dispatch(handleUpdateCreateExplore(id, { ...saveHex, ...n }));
          dispatch(reportReducer.saveExploration(id));

          if (skipUpdateTab !== true) {
            if (isChatGPT) {
              const chatApp = chatGPTReducer.getChatApp(getState());
              const currentActiveTab = nextReducer.getActiveTab(getState(), id);
              if (chatApp?.wizard?.startInDashboard === true) {
                dispatch(nextReducer.updateActiveTab(id, "NLQ Dashboard"));
              } else if (currentActiveTab !== "CHATGPT" && currentActiveTab !== "NLQ Dashboard") {
                dispatch(nextReducer.updateActiveTab(id, "CHATGPT"));
              }
            } else if (comparisonOptions) {
              dispatch(nextReducer.updateActiveTab(id, fBlueprint?.wizard?.appStartViewType ?? "DASHBOARD"));
            } else {
              const defaultView = nextReducer.getExploreDefaultViewMode(getState());
              dispatch(nextReducer.updateActiveTab(id, defaultView));
            }
          }

          let autoSavePromise = Promise.resolve();
          if (isEnabled("next-autosave") && !skipAutoSave) {
            autoSavePromise = dispatch(nextReducer.saveNext(id));
          }

          return autoSavePromise.then(() => {
            if (
              isChatGPT &&
              !skipAskIntro &&
              allowSkipSecuritySettings &&
              chatGPTReducer.getSkipSecurityConfirm(getState())
            ) {
              return dispatch(chatGPTReducer.askIntro());
            }
            const isTemplateLoading = globalReducer.getIsTemplateLoading(getState());
            if (isTemplateLoading) {
              dispatch(globalReducer.updateIsTemplateLoading(false));
            }
            return Promise.resolve();
          });
        });
    } else {
      if (skipUpdateTab !== true) {
        if (isChatGPT) {
          const chatApp = chatGPTReducer.getChatApp(getState());
          if (chatApp?.wizard?.startInDashboard === true) {
            dispatch(nextReducer.updateActiveTab(id, "NLQ Dashboard"));
          } else {
            dispatch(nextReducer.updateActiveTab(id, "CHATGPT"));
          }
        } else if (comparisonOptions) {
          dispatch(nextReducer.updateActiveTab(id, fBlueprint?.wizard?.appStartViewType ?? "DASHBOARD"));
        } else {
          const defaultView = nextReducer.getExploreDefaultViewMode(getState());
          dispatch(nextReducer.updateActiveTab(id, defaultView));
        }
      }

      if (
        isChatGPT &&
        !skipAskIntro &&
        allowSkipSecuritySettings &&
        chatGPTReducer.getSkipSecurityConfirm(getState())
      ) {
        return await dispatch(chatGPTReducer.askIntro());
      }

      const isTemplateLoading = globalReducer.getIsTemplateLoading(getState());
      if (isTemplateLoading) {
        dispatch(globalReducer.updateIsTemplateLoading(false));
      }
      return Promise.resolve();
    }
  };
}

export function updateSelectedHex(id, hex, n) {
  return (dispatch, getState) => {
    let hexMap = { ...getHexMap(getState(), id) };

    const selectedHex = getSelectedHex(getState(), id);

    if (n && n.leftSection && (n.leftSection === "CREATE" || n.leftSection === "FORECAST")) {
      return dispatch(createProjectHelper(id, hex, n));
    }

    if (selectedHex && selectedHex.leftSection === "OUTCOME") {
      dispatch(handleBack(id));
    }

    if (n && n.leftSection && n.leftSection === "EXPLORE") {
      const datasetId = nextReducer.getDatasetId(getState(), id);

      dispatch(nextReducer.init("new", datasetId));

      dispatch(push("/next/" + datasetId + "/new"));

      const comparisonOptions = nextReducer.getComparisonOptions(getState(), id);
      if (comparisonOptions) {
        return dispatch(nextReducer.updateActiveTab(id, "EXPLORE"));
      }
      const defaultView = nextReducer.getExploreDefaultViewMode(getState());
      return dispatch(nextReducer.updateActiveTab("new", defaultView));
    }

    if (n && n.leftSection && n.leftSection === "CREATE_EXPLORE") {
      return dispatch(fullCreateExplore(id, n));
    }

    if (n && n.leftSection && n.leftSection === "OPEN_EXPLORE") {
      dispatch(nextReducer.updateExplorationKey(id, n.explorationId));
      dispatch(handleBack(id));
      const comparisonOptions = nextReducer.getComparisonOptions(getState(), id);
      if (comparisonOptions) {
        return dispatch(nextReducer.updateActiveTab(id, "EXPLORE"));
      }
      const defaultView = nextReducer.getExploreDefaultViewMode(getState());
      return dispatch(nextReducer.updateActiveTab(id, defaultView));
    }

    if (selectedHex && selectedHex.x === hex.x && selectedHex.y === hex.y) {
      dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: undefined
      });

      dispatch(updateActionHexMap(id, {}));

      return dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
    }

    let actionHexMap = { ...getActionHexMap(getState(), id) };
    const actionNode = actionHexMap[hex.x + "_" + hex.y];

    if (actionNode) {
      return dispatch({
        type: UPDATE_FIELD_VALUE,
        id: id,
        key: "selectedHex",
        data: { ...hex, ...n }
      });
    }

    let node = hexMap[hex.x + "_" + hex.y];

    if (node === undefined) {
      const sNode = getSavedHexMap(getState(), id)[hex.x + "_" + hex.y];

      if (sNode && sNode.length > 0 && sNode[0].parent) {
        node = sNode[0].parent;
      }
    }

    if (node && node.isSave) {
      const dTile = document.getElementById("hexTile_" + hex.x + "_" + hex.y);
      const pTile = document.getElementById("hexTile_" + node.parent.x + "_" + node.parent.y);

      if (dTile && pTile) {
        dTile.style.left = pTile.style.left;
        dTile.style.top = pTile.style.top;
      }

      dispatch(
        updateHexMap(id, {
          ...getInitialHexMap(getState(), id),
          [hex.x + "_" + hex.y]: node
        })
      );

      return setTimeout(() => {
        let savedHexMap = getSavedHexMap(getState(), id);

        if (savedHexMap[node.parent.x + "_" + node.parent.y] === undefined) {
          savedHexMap[node.parent.x + "_" + node.parent.y] = [node];
        } else {
          savedHexMap[node.parent.x + "_" + node.parent.y].push(node);
        }

        dispatch({
          type: UPDATE_FIELD_VALUE,
          id: id,
          key: "selectedHex",
          data: { x: 3, y: 3 }
        });
        dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
        return dispatch(updateSavedHexMap(id, savedHexMap));
      }, 200);
    }

    /*
    if (node && node.selected) {
      hexMap = { ...initTransform.hexMap, [hex.x + "_" + hex.y]: node };
    }
    */

    const savedHexMap = getSavedHexMap(getState(), id);

    const savedCreateKey = Object.keys(savedHexMap).find((k) => savedHexMap[k]?.leftSection === "CREATE");
    if (node?.leftSection !== "OUTCOME" || !savedCreateKey) {
      const neighbors = getNeighborLocation(hex.x, hex.y);

      const topActions = { ...getTopActions(getState(), id) };

      let nIndex = 0;
      for (let nKey of Object.keys(neighbors)) {
        const neighbor = neighbors[nKey];

        if (node && node.selected) {
          const selected = getTopActions(getState(), id)[node.index]?.selected;

          if (selected[nIndex]) {
            const foundNodes = Object.keys(savedHexMap).filter((h) => savedHexMap[h].text === selected[nIndex].text);

            const rKey = foundNodes.length > 0 ? foundNodes[0] : neighbor.x + "_" + neighbor.y;

            actionHexMap[rKey] = {
              ...selected[nIndex],
              parent: { ...hex, ...node },
              color: node.color
            };
          }
        } else {
          if (nIndex < 6 && getTopActions(getState(), id)[nIndex]) {
            actionHexMap[neighbor.x + "_" + neighbor.y] = {
              ...getTopActions(getState(), id)[nIndex]
            };
          }
        }

        nIndex++;
      }
    }

    actionHexMap[hex.x + "_" + hex.y] = node;

    dispatch({
      type: UPDATE_FIELD_VALUE,
      id: id,
      key: "selectedHex",
      data: { ...hex, ...n }
    });

    if (n?.afterSetup === undefined) {
      dispatch(updateActionHexMap(id, actionHexMap));
    }

    return Promise.resolve();
    //dispatch(updateHexMap(id, hexMap));

    // Sub action
    /*
    if (node && node.selected && node.parent) {
      for (const hKey of Object.keys(hexMap)) {
        const hNode = hexMap[hKey];

        if (
          hNode &&
          (hNode.isSub || hNode.icon) &&
          selectedHex &&
          hKey !== selectedHex.x + "_" + selectedHex.y &&
          hKey !== hex.x + "_" + hex.y
        ) {
          hexMap[hKey] = undefined;
        }
      }

      dispatch(updateHexMap(id, hexMap));
    } else if (node && node.selected) {
      for (const hKey of Object.keys(hexMap)) {
        const hNode = hexMap[hKey];

        if (
          hNode &&
          hNode.icon &&
          hKey !== hex.x + "_" + hex.y
        ) {
          hexMap[hKey] = undefined;
        }
      }
    }
    */
  };
}

export function getModelCount(state, id) {
  const projectId = nextReducer.getProjectId(state, id);
  const modelCount = projectReducer.getTotalModelCount(state, projectId);

  return modelCount;
}

export function getTopActions(state, id) {
  const datasetId = nextReducer.getDatasetId(state, id);
  const dataset = homeReducer.getDataset(state, datasetId);
  const outcome = dataset?.fields[dataset?.outcome];

  const projectId = nextReducer.getProjectId(state, id);

  let topActions = isEnabled("aible-explore")
    ? {
        0: {
          index: 0,
          text: "I want to explore insights",
          gridType: "Explore",
          selected: {
            0: {
              text: "I want Aible to explore all insights about " + outcome?.label,
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "Default",
              isPurple: true,
              icon: "Compass"
            },
            1: {
              text: "I want to understand the top 6 variables that affect " + outcome?.label,
              isSave: true,
              gridType: "Reduced",
              leftSection: "CREATE_EXPLORE",
              isPurple: true,
              icon: "Checklist"
            },
            2: {
              text: "I want to look at the top variables that affect my model",
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "ModelDriver",
              isPurple: true,
              icon: "Soundwave"
            },
            3: {
              text: "I want to manually explore all insights about " + outcome?.label,
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "Empty",
              isPurple: true,
              icon: "Worker"
            }
          },
          childDelta: { x: 1, y: 0 },
          oChildDelta: { x: 1, y: 1 },
          background: "#7E69D7",
          color: "white",
          icon: "Explore"
        },
        1: {
          index: 1,
          text: "I want to transform the data",
          leftSection: "TRANSFORM",
          selected: {},
          icon: "Transform",
          background: "#7E69D7",
          color: "white"
        }
      }
    : {
        0: {
          index: 0,
          text: "I want to explore insights",
          gridType: "Explore",
          selected: {
            0: {
              text: "I want Aible to explore all insights about " + outcome?.label,
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "Default",
              isPurple: true,
              icon: "Compass"
            },
            1: {
              text: "I want to understand the top 6 variables that affect " + outcome?.label,
              isSave: true,
              gridType: "Reduced",
              leftSection: "CREATE_EXPLORE",
              isPurple: true,
              icon: "Checklist"
            },
            2: {
              text: "I want to look at the top variables that affect my model",
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "ModelDriver",
              isPurple: true,
              icon: "Soundwave"
            },
            /*3: {
          text:
            "I want to understand ways to simplify this dataset for AI training",
          isSave: true,
          leftSection: "CREATE_EXPLORE",
          gridType: "Reverse",
          isPurple: true,
        },*/
            3: {
              text: "I want to manually explore all insights about " + outcome?.label,
              isSave: true,
              leftSection: "CREATE_EXPLORE",
              gridType: "Empty",
              isPurple: true,
              icon: "Worker"
            }
            /*
        5: {
          text: "Show all my saved explorations",
          isSave: true,
          leftSection: "CREATE_EXPLORE",
          isPurple: true,
        },
        */
          },
          childDelta: { x: 1, y: 0 },
          oChildDelta: { x: 1, y: 1 },
          background: "#7E69D7",
          color: "white",
          icon: "Explore"
        },
        1: {
          index: 1,
          text: "I want to transform the data",
          leftSection: "TRANSFORM",
          selected: {
            // 0: { text: "Change Data Shape (Log of X, Box Cox, etc)", isSave: true },
            // 1: { text: "Arithmetic Functions (x / y, y + x, etc)", isSave: true },
            // 2: { text: "Aggregation", isSave: true },
            // 3: { text: "Data Transform", isSave: true },
            // 4: { text: "Manually Specify", isSave: true },
          },
          icon: "Transform",
          background: "#7E69D7",
          color: "white"
        },
        2: {
          index: 2,
          text: "I have a specific use case I want to focus on",
          selected: {},
          background: "#7E69D7",
          color: "white",
          icon: "Blueprint",
          leftSection: "BLUEPRINT"
          // hasUpdate: true,
        },
        3: {
          index: 3,
          text: "I want to create AI",
          selected: {
            0: {
              text: "Create AI Models",
              isSave: true,
              leftSection: "CREATE"
            },
            1: {
              text: "Select Outcome and Filter",
              isSave: true,
              leftSection: "OUTCOME",
              hasUpdate: true,
              color: "#333"
            }
          },
          leftSection: "OUTCOME",
          background: "#7E69D7",
          color: "white",
          icon: "Ai Training"
        }
        // 4: {
        //   index: 4,
        //   type: nextReducer.HEXAGON_NODES.ADD_JOB,
        //   leftSection: "ADD_JOB",
        //   text: "I want to post a job automatically",
        //   selected: {},
        //   background: "#7E69D7",
        //   color: "white",
        // },
      };

  if (outcome?.data && topActions?.[3]) {
    topActions[3].selected = {};

    let lastIndex = 0;
    if (outcome?.type !== "number") {
      outcome?.data.forEach((d, i) => {
        topActions[3].selected[i] = {
          text: "Create AI Models",
          afterText: "Creating AI",
          subText: "Predict " + outcome?.label + " is",
          success: d,
          boldText: d.value,
          isSave: true,
          leftSection: "CREATE",
          color: "#333"
        };

        lastIndex = i;
      });
    } else {
      if (!projectReducer.getIsForecastProject(state, projectId)) {
        topActions[3].selected[0] = {
          text: "Create AI Models",
          afterText: "Creating AI",
          subText: "Predict " + outcome?.label,
          success: {
            value: outcome?.label,
            type: "number"
          },
          boldText: "",
          isSave: true,
          leftSection: "CREATE",
          color: "#333"
        };
      }
      lastIndex = 0;
      if (homeReducer.getIsForecastReady(state, datasetId)) {
        const forecastOptions = homeReducer.getForecastOptions(state, datasetId);
        forecastOptions.forEach((option) => {
          if (lastIndex < 4) {
            lastIndex++;
            topActions[3].selected[lastIndex] = {
              text: "Forecasting",
              afterText: "Creating AI",
              subText: "Forecast " + outcome?.label + " using " + option.label,
              success: {
                value: outcome?.label,
                type: "number",
                column: option.value,
                isForecast: true
              },
              boldText: "",
              isSave: true,
              leftSection: "FORECAST",
              color: "#333"
            };
          }
        });
      }
    }

    /*
    topActions[1].selected[lastIndex + 1] = {
      text: "I want to predict something different",
      icon: "OppositeArrows",
    };
    */

    /*
    topActions[1].selected[lastIndex + 3] = {
      text: "Post a job Automatically",
      type: nextReducer.HEXAGON_NODES.ADD_JOB,
      leftSection: "ADD_JOB",
      color: "rgb(126, 105, 215)",
    };
    */
  }

  /*
  const explorations = reportReducer.getExplorations(state, id);

  for (let i = 0; i < Object.keys(explorations).length; i++) {
    topActions[2].selected[i + 1] = {
      leftSection: "OPEN_EXPLORE",
      nextId: id,
      explorationId: i,
      title: explorations[i].reportLabel,
      hexMap: explorations[i].hexMap,
    };
  }
  */

  return topActions;
}

const initTransform = {
  hexMap: {
    [8 + "_" + 8]: {
      text: "Dataset Name",
      color: "white",
      leftSection: "OUTCOME",
      background: "#34D399"
    }
  },
  savedHexMap: {},
  actionHexMap: {}
};

export function updateTransform(id, transform) {
  return { type: UPDATE_FIELD, id: id, data: transform };
}

export function initAll() {
  return (dispatch, getState) => {
    const reports = reportReducer.getReports(getState());

    reports &&
      reports.forEach((report) => {
        if (report.metadata.transforms && Object.keys(report.metadata.transforms).length > 0) {
          dispatch(
            updateTransform(report.id, {
              ...report.metadata.transforms
            })
          );
        } else {
          dispatch(init(report.id));
        }
      });

    return Promise.resolve();
  };
}

export function init(id) {
  return (dispatch, getState) => {
    const retInitTransform = {
      hexMap: { ...initTransform.hexMap },
      actionHexMap: { ...initTransform.actionHexMap },
      savedHexMap: { ...initTransform.savedHexMap }
    };

    dispatch(updateTransform(id, { ...retInitTransform }));
    return dispatch(updateHexMap(id, getInitialHexMap(getState(), id)));
  };
}

export default transformReducer;
